import Response from "./response";

// Modules
import { buildQueryString } from "@/modules/query_string";

const csrfToken = () => (
  document.querySelector("meta[name='csrf-token']")?.getAttribute("content")
);

const buildFormBody = (data = {}) => (
  new URLSearchParams(buildQueryString(data))
);

const parseJsonResponse = async (response) => {
  const text = await response.text();

  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch (_error) {
    return text;
  }
};

const isFormData = (value) => (
  typeof FormData !== "undefined" &&
  value instanceof FormData
);

const isUrlSearchParams = (value) => (
  typeof URLSearchParams !== "undefined" &&
  value instanceof URLSearchParams
);

const isBlob = (value) => (
  typeof Blob !== "undefined" &&
  value instanceof Blob
);

const isArrayBuffer = (value) => (
  typeof ArrayBuffer !== "undefined" &&
  value instanceof ArrayBuffer
);

const shouldJsonEncodeBody = (body) => (
  body !== undefined &&
  body !== null &&
  typeof body === "object" &&
  !isFormData(body) &&
  !isUrlSearchParams(body) &&
  !isBlob(body) &&
  !isArrayBuffer(body)
);

export default class Query {
  constructor(klass) {
    this._klass = klass;
  }

  static request = async (url, options = {}) => {
    const {
      params,
      body,
      form,
      headers: optionHeaders,
      ...fetchOptions
    } = options;

    let requestUrl = url;

    if (params) {
      const queryString = buildQueryString(params);

      if (queryString) {
        requestUrl += requestUrl.includes("?")
          ? `&${queryString}`
          : `?${queryString}`;
      }
    }

    const hasFormBody =
      form !== undefined &&
      form !== null;

    const jsonBody =
      !hasFormBody &&
      shouldJsonEncodeBody(body);

    const requestBody = hasFormBody
      ? buildFormBody(form)
      : jsonBody
        ? JSON.stringify(body)
        : body;

    const headers = {
      Accept: "application/json",
      ...(jsonBody
        ? { "Content-Type": "application/json" }
        : {}),
      ...(csrfToken()
        ? { "X-CSRF-Token": csrfToken() }
        : {}),
      ...(optionHeaders || {}),
    };

    const response = await fetch(requestUrl, {
      credentials: "same-origin",
      ...fetchOptions,
      ...(requestBody !== undefined
        ? { body: requestBody }
        : {}),
      headers,
    });

    const parsedResponse = await parseJsonResponse(response);

    if (!response.ok) {
      console.log("Error: ", parsedResponse);

      throw {
        status: response.status,
        statusText: response.statusText,
        responseJSON: parsedResponse,
        responseText:
          typeof parsedResponse === "string"
            ? parsedResponse
            : JSON.stringify(parsedResponse),
      };
    }

    return parsedResponse;
  }

  perPage = (perPage) => {
    this._perPage = perPage;
    return this;
  }

  page = (page) => {
    this._page = page;
    return this;
  }

  where = (q) => {
    this._q = { ...this._q, ...q };
    return this;
  }

  sort = (sort) => {
    this._q = { ...this._q, s: sort };
    return this;
  }

  url = (url) => {
    this._url = url;
    return this;
  }

  params = (params) => {
    this._params = params;
    return this;
  }

  fields = (fields) => {
    this._fields = fields;
    return this;
  }

  assign = (props) => {
    Object.assign(this._klass, props);
    return this;
  }

  klassName = () => (
    (new this._klass()).constructor.name
  )

  idField = () => (
    this._klass.ID_FIELD || "id"
  )

  /**
   * find - Returns a promise that resolves to one API result.
   * @param {number|string} id - ID belonging to the resource to load
   */
  find = (id) => (
    new Promise((resolve, reject) => {
      if (!id) {
        console.log(
          "Error",
          `${this.klassName()}.${this.idField()} is required for find.`
        );

        resolve(new this._klass({}));
        return;
      }

      this.get(id)
        .then((response) => {
          resolve(new this._klass(response));
        })
        .catch(reject);
    })
  )

  /**
   * all - Returns API results with or without pagination.
   * @param {bool} pagination - whether to include pagination
   * @returns {Promise}
   */
  all = (pagination = true) => (
    new Promise((resolve, reject) => {
      this.get()
        .then((response) => {
          const results = this.buildObjects(response);

          if (pagination) {
            resolve(new Response(
              response.total_entries,
              response.total_pages,
              response.previous_page,
              response.next_page,
              results,
              response.meta || {}
            ));
          } else {
            resolve(results);
          }
        })
        .catch(reject);
    })
  )

  /**
   * save - Posts or patches based on the configured id field.
   * @param {object} obj - Object to be updated/created
   * @returns {Promise}
   */
  save = (obj) => {
    const promise = obj[this.idField()]
      ? this.patch(obj[this.idField()], obj)
      : this.post(obj);

    return new Promise((resolve, reject) => {
      promise
        .then((response) => {
          resolve(new this._klass(response));
        })
        .catch(reject);
    });
  }

  update = (obj, data) => (
    new Promise((resolve, reject) => {
      const id = obj[this.idField()];

      if (!id) {
        const error =
          `${this.klassName()}.${this.idField()} is required for update.`;

        reject({
          responseText: error,
          responseJSON: {
            id: [error],
            obj,
            data,
          },
        });

        return;
      }

      this.patch(id, data)
        .then((response) => {
          resolve(new this._klass(response));
        })
        .catch(reject);
    })
  )

  destroy = (obj) => (
    new Promise((resolve, reject) => {
      const id = obj[this.idField()];

      if (!id) {
        const error =
          `${this.klassName()}.${this.idField()} is required for destroy.`;

        reject({
          responseText: error,
          responseJSON: {
            id: [error],
            obj,
          },
        });

        return;
      }

      this.delete(obj[this.idField()])
        .then(() => {
          resolve();
        })
        .catch(reject);
    })
  )

  get = (id) => {
    let url = this.requestURL;

    if (id) {
      url = `${url}/${id}`;
    }

    const params = {
      per_page: this.calculatedPerPage,
      page: this._page || 1,
      q: this._q || {},
      fields: this._fields,
      ...(this._params || {}),
    };

    return this.request(url, {
      method: "GET",
      params,
    });
  }

  post = (data) => {
    const objectName = this.responseObjectName;

    return this.request(this.requestURL, {
      method: "POST",
      body: {
        [objectName]: data,
        ...(this._params || {}),
      },
    });
  }

  patch = (id, data) => {
    const objectName = this.responseObjectName;

    return this.request(`${this.requestURL}/${id}`, {
      method: "PATCH",
      body: {
        [objectName]: data,
        ...(this._params || {}),
      },
    });
  }

  delete = (id) => {
    return this.request(`${this.requestURL}/${id}`, {
      method: "DELETE",
      params: this._params || {},
    });
  }

  request = (url, options = {}) => (
    Query.request(url, options)
  )

  get calculatedPerPage() {
    if (
      this._perPage === 0 ||
      (
        this._perPage !== undefined &&
        this._perPage !== null
      )
    ) {
      return this._perPage;
    }

    return Number.MAX_SAFE_INTEGER;
  }

  get requestURL() {
    return this._url
      ? this._url
      : this._klass.classUrl();
  }

  buildObjects = (response) => (
    response[this.responseCollectionName] || []
  ).map(
    (object) =>
      new this._klass(object)
  )

  get responseObjectName() {
    return this._klass.NAME;
  }

  get responseCollectionName() {
    if (!this._klass.COLLECTION_NAME) {
      throw new Error(
        `${this.klassName()}.COLLECTION_NAME is required`
      );
    }

    return this._klass.COLLECTION_NAME;
  }
}
